import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface SeniorLocationRow {
  lat: number;
  lon: number;
  accuracy_meters: number;
  updated_at: string;
}

/**
 * F03 — reads senior_location for the given senior + subscribes to realtime
 * UPDATE/INSERT events. RLS scopes SELECT to caregivers' own seniors only.
 *
 * Returns:
 *   `location`: row when known, null when no row yet (sharing not enabled or
 *               phone hasn't reported). Panel renders the "Location unavailable"
 *               state on null.
 *   `loading`:  true on first fetch.
 */
export function useSeniorLocation(seniorId: string | undefined) {
  const [location, setLocation] = useState<SeniorLocationRow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!seniorId) {
      setLocation(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchOnce = async () => {
      const { data, error } = await supabase
        .from('senior_location')
        .select('lat, lon, accuracy_meters, updated_at')
        .eq('senior_user_id', seniorId)
        .maybeSingle();

      if (cancelled) return;
      if (error) {
        console.warn('[useSeniorLocation] fetch error:', error.message);
        setLocation(null);
      } else {
        setLocation(data as SeniorLocationRow | null);
      }
      setLoading(false);
    };

    setLoading(true);
    fetchOnce();

    const channel = supabase
      .channel(`senior_location_${seniorId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'senior_location', filter: `senior_user_id=eq.${seniorId}` },
        (payload) => {
          const row = payload.new as SeniorLocationRow | null;
          if (row && typeof row.lat === 'number' && typeof row.lon === 'number') {
            setLocation(row);
          }
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [seniorId]);

  return { location, loading };
}
